import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Flex } from '@decisiv/ui-components';
import { H2, P } from '@decisiv/ui-components/lib/components/Typography';

import AdminPage from 'lib/admin-kit/components/AdminPage';
import AdminPanel from 'lib/admin-kit/components/AdminPanel';
import ErrorBox from 'lib/admin-kit/components/ErrorBox';
import useAdminMutation from 'lib/admin-kit/hooks/useAdminMutation';

// Bespoke, not createAdminResource-based: a form plus a result view, not an
// indexed CRUD collection. Follows pages/admin/ai_settings.
const ENDPOINT = '/internal_admin/admin_kit/pcaraz_invitations';
const POLL_MS = 2000;

// The run keeps going in the background whatever the polling does, so a polling
// failure must not read as a failed run.
const STILL_SENDING = 'The invitations may still be sending — reload to check again.';

const GROUPS = [
  { outcome: 'invited', label: 'Invited' },
  { outcome: 'not_found', label: 'No matching user' },
  { outcome: 'failed', label: 'Failed' },
];

const resultsFor = (run, outcome) =>
  (run?.results ?? []).filter((r) => r.outcome === outcome);

const PcarazInvitationsNew = () => {
  const [emails, setEmails] = useState('');
  const [runId, setRunId] = useState(null);
  const [run, setRun] = useState(null);
  const [errors, setErrors] = useState([]);
  const timer = useRef(null);

  const stopPolling = useCallback(() => {
    if (timer.current) {
      window.clearInterval(timer.current);
      timer.current = null;
    }
  }, []);

  const { mutate, isSubmitting } = useAdminMutation({
    onSuccess: (payload) => {
      setRunId(payload.run_id);
      setRun({ total: payload.total, finished: false, results: [] });
    },
    onError: (err) => {
      try {
        const body = JSON.parse(err.message);
        setErrors(body.errors ?? [err.message]);
      } catch (_) {
        setErrors([err.message]);
      }
    },
  });

  useEffect(() => {
    if (!runId) return undefined;

    // Only 404 means the run is gone. Anything else — a lapsed session giving
    // 403, a 500, an unreachable server — leaves the run still sending in the
    // background, so say that rather than reporting it as expired.
    const poll = async () => {
      try {
        const res = await window.fetch(`${ENDPOINT}/status/${runId}`);

        if (res.status === 404) {
          stopPolling();
          setErrors(['That run has expired, so its results are no longer available.']);
          return;
        }

        if (!res.ok) {
          stopPolling();
          setErrors([`Could not read this run's progress (HTTP ${res.status}). ${STILL_SENDING}`]);
          return;
        }

        const payload = await res.json();
        setRun(payload);
        if (payload.finished) stopPolling();
      } catch (_) {
        // Without this the rejection is unhandled and repeats on every tick.
        stopPolling();
        setErrors([`Could not reach the server to read this run's progress. ${STILL_SENDING}`]);
      }
    };

    poll();
    timer.current = window.setInterval(poll, POLL_MS);

    // On unmount as well as on completion: a leaked interval keeps polling for
    // as long as the tab stays open.
    return stopPolling;
  }, [runId, stopPolling]);

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    setErrors([]);
    mutate(ENDPOINT, { method: 'POST', body: { emails } });
  };

  const processed = run?.results?.length ?? 0;

  return (
    <AdminPage
      title="Send Auth0 Invitation"
      breadcrumbs={[{ text: 'SSO' }, { text: 'Send Auth0 Invitation' }]}
    >
      <AdminPanel title="Email addresses">
        <form onSubmit={handleSubmit}>
          <P color="alternative">
            Paste the addresses to re-invite, separated by commas. Each one gets a
            fresh Auth0 invitation email.
          </P>
          <textarea
            aria-label="Email addresses"
            rows={10}
            value={emails}
            disabled={isSubmitting || !!runId}
            onChange={(event) => setEmails(event.target.value)}
            style={{ width: '100%', fontFamily: 'monospace', padding: 8 }}
          />
          <ErrorBox errors={errors} />
          <Flex gap={1} marginTop={2}>
            <Button
              text="Send Invitations"
              kind="primary"
              loading={isSubmitting}
              loadingText="Starting…"
              disabled={isSubmitting || !!runId}
              onClick={handleSubmit}
            />
          </Flex>
        </form>
      </AdminPanel>

      {run && (
        <AdminPanel title="Results">
          <P>
            {run.finished
              ? `Finished — ${processed} of ${run.total} addresses processed.`
              : `Working — ${processed} of ${run.total} addresses processed.`}
          </P>

          {GROUPS.map(({ outcome, label }) => {
            const rows = resultsFor(run, outcome);
            return (
              <Flex key={outcome} flexDirection="column" gap={0.5} marginTop={2}>
                <H2 style={{ fontSize: 16, margin: 0 }}>
                  {label} ({rows.length})
                </H2>
                {rows.map((row) => (
                  <P key={`${outcome}-${row.email}`} style={{ margin: 0 }}>
                    {row.email}
                    {row.message ? ` — ${row.message}` : ''}
                  </P>
                ))}
              </Flex>
            );
          })}
        </AdminPanel>
      )}
    </AdminPage>
  );
};

export default PcarazInvitationsNew;
